// GST Centre — dedicated section with 7 tabs (Pending/Generated/Failed/Cancelled
// E-Invoices + Pending/Generated/Failed E-Way Bills). Backed by /api/gst/* endpoints.

import { useEffect, useState, useMemo } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast, Toaster } from "sonner";
import { FileCheck2, FileX2, Truck, AlertTriangle, CircleSlash, CheckCircle2, Send, X, RefreshCw, PenLine, Search, Info } from "lucide-react";

const TABS = [
  { key: "ei_pending",    label: "Pending E-Invoice",   group: "ei",  status: "pending",   icon: AlertTriangle, color: "amber" },
  { key: "ei_generated",  label: "Generated E-Invoice", group: "ei",  status: "generated", icon: FileCheck2,    color: "emerald" },
  { key: "ei_failed",     label: "Failed E-Invoice",    group: "ei",  status: "failed",    icon: FileX2,        color: "rose" },
  { key: "ei_cancelled",  label: "Cancelled E-Invoice", group: "ei",  status: "cancelled", icon: CircleSlash,   color: "slate" },
  { key: "ewb_pending",   label: "Pending E-Way Bill",  group: "ewb", status: "pending",   icon: AlertTriangle, color: "amber" },
  { key: "ewb_generated", label: "Generated E-Way Bill",group: "ewb", status: "generated", icon: Truck,         color: "emerald" },
  { key: "ewb_failed",    label: "Failed E-Way Bill",   group: "ewb", status: "failed",    icon: FileX2,        color: "rose" },
];

// Per-tab Action-column filter options. Each entry: [value, label, predicate(row)].
// The dropdown lets operators slice the current tab by the action that's
// actionable on each row — e.g. on "E-Invoice · Pending" they can isolate
// only the B2B invoices that absolutely need an IRN (skipping B2C / low
// value), and on "Failed" they can split by retry strategy (Retry vs Fetch
// Existing vs Manual). Labels mirror the actual action-column button text
// where possible so the operator's mental model matches what they see.
//
// IRN-mandatory threshold: ₹50,000 — used to flag invoices that MUST get
// an IRN before they can be issued. EWB threshold is identical.
const IRN_MIN = 50000;
const EWB_MIN = 50000;
const _isB2B = (r) => !!(r.client_gstin || r.gst_number || r.buyer_gstin);
const _ageDays = (r) => {
  const d = r.invoice_date || r.created_at;
  if (!d) return 0;
  const t = new Date(d).getTime();
  if (isNaN(t)) return 0;
  return (Date.now() - t) / (1000 * 3600 * 24);
};

const ACTION_FILTERS = {
  ei_pending: [
    ["all",         "All actions",                  () => true],
    ["b2b",         "B2B · Has client GSTIN",       (r) => _isB2B(r)],
    ["b2c",         "B2C · No client GSTIN",        (r) => !_isB2B(r)],
    ["mandatory",   `Mandatory IRN (≥ ₹${IRN_MIN.toLocaleString("en-IN")})`, (r) => (r.total_amount || 0) >= IRN_MIN && _isB2B(r)],
    ["overdue",     "Overdue · > 7 days old",       (r) => _ageDays(r) > 7],
  ],
  ei_generated: [
    ["all",          "All actions",                      () => true],
    ["qr-missing",   "Action · Refresh QR",              (r) => !r.signed_qr_code],
    ["cancellable",  "Action · Cancel IRN available",    (r) => r.einvoice_status === "Generated" && r.cancel_status !== "Cancelled"],
    ["complete",     "Fully complete · IRN + QR",        (r) => !!r.signed_qr_code && r.einvoice_status === "Generated"],
  ],
  ei_failed: [
    ["all",        "All actions",                          () => true],
    ["duplicate",  "Action · Fetch Existing (NIC 2150)",   (r) => /\b2150\b|duplicate/i.test(r.einvoice_error || "")],
    ["retryable",  "Action · Retry IRN",                   (r) => !/\b2150\b|duplicate/i.test(r.einvoice_error || "")],
    ["mandatory",  `Mandatory IRN (≥ ₹${IRN_MIN.toLocaleString("en-IN")})`, (r) => (r.total_amount || 0) >= IRN_MIN && _isB2B(r)],
  ],
  ei_cancelled: [
    ["all",       "All actions",                () => true],
    ["recent",    "Cancelled in last 24h",      (r) => {
      const ts = r.cancel_date || r.cancelled_at;
      if (!ts) return false;
      const t = new Date(ts).getTime();
      return !isNaN(t) && (Date.now() - t) < 24 * 3600 * 1000;
    }],
  ],
  ewb_pending: [
    ["all",         "All actions",                       () => true],
    ["b2b",         "B2B · Has client GSTIN",            (r) => _isB2B(r)],
    ["mandatory",   `Mandatory EWB (≥ ₹${EWB_MIN.toLocaleString("en-IN")})`, (r) => (r.total_amount || 0) >= EWB_MIN && _isB2B(r)],
    ["overdue",     "Overdue · > 7 days old",            (r) => _ageDays(r) > 7],
  ],
  ewb_generated: [
    ["all",            "All actions",          () => true],
    ["expiring-soon",  "Expiring in < 24h",    (r) => {
      if (!r.valid_until) return false;
      const t = new Date(r.valid_until).getTime();
      return !isNaN(t) && (t - Date.now()) > 0 && (t - Date.now()) < 24 * 3600 * 1000;
    }],
    ["expired",        "Expired",              (r) => {
      if (!r.valid_until) return false;
      const t = new Date(r.valid_until).getTime();
      return !isNaN(t) && t < Date.now();
    }],
    ["fresh",          "Valid · > 24h remaining",  (r) => {
      if (!r.valid_until) return false;
      const t = new Date(r.valid_until).getTime();
      return !isNaN(t) && (t - Date.now()) >= 24 * 3600 * 1000;
    }],
  ],
  ewb_failed: [
    ["all",       "All actions",                                () => true],
    ["mandatory", `Mandatory EWB (≥ ₹${EWB_MIN.toLocaleString("en-IN")})`, (r) => (r.total_amount || 0) >= EWB_MIN && _isB2B(r)],
    ["b2b",       "B2B · Has client GSTIN",                     (r) => _isB2B(r)],
  ],
};

const CANCEL_REASONS = [
  { code: "1", label: "Duplicate" },
  { code: "2", label: "Data Entry Mistake" },
  { code: "3", label: "Order Cancelled" },
  { code: "4", label: "Others" },
];

export default function GSTPage() {
  const [tab, setTab] = useState("ei_pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [ewbFor, setEwbFor] = useState(null);
  // Free-text search + action-aware filter (added 2026-02-22). Both reset
  // when the tab changes so operators don't carry "Cancellable" into the
  // Failed tab and get confused by an empty grid.
  const [search, setSearch] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  // Failed E-Invoice recovery (added 2026-02-21):
  //   • Fetch Existing IRN  → calls NIC GetIRNByDocDtls (for 2150 duplicates)
  //   • Manual IRN Entry    → admin pastes IRN/Ack No/Ack Date from NIC portal
  const [fetchFor, setFetchFor] = useState(null);
  const [fetchBusy, setFetchBusy] = useState(false);
  const [manualFor, setManualFor] = useState(null);
  // Production-mode safety gate (added 2026-06-20 per Radiants policy):
  // when the current GST mode is `production`, the Generate IRN button
  // does NOT fire immediately — it opens a typed-confirmation modal that
  // requires the user to type the exact word `GENERATE` before the
  // request is sent. Sandbox skips the gate entirely (fire & forget).
  const [prodConfirmFor, setProdConfirmFor] = useState(null); // row to generate
  const [prodConfirmText, setProdConfirmText] = useState("");
  const [prodSubmitting, setProdSubmitting] = useState(false);

  const t = useMemo(() => TABS.find(x => x.key === tab), [tab]);
  const actionOptions = ACTION_FILTERS[tab] || [["all", "All actions", () => true]];

  // Reset filters on tab switch so a stale predicate from a different tab
  // doesn't blank out the new view (e.g. "Cancellable" makes no sense on
  // the Failed tab).
  useEffect(() => { setSearch(""); setActionFilter("all"); }, [tab]);

  // Combined free-text + action filter, applied client-side. Backend already
  // narrows to the status of the active tab via `?status=` — this layer is
  // pure UX.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const predicate = (actionOptions.find(o => o[0] === actionFilter) || actionOptions[0])[2];
    return (rows || []).filter(r => {
      if (!predicate(r)) return false;
      if (!q) return true;
      const haystack = [
        r.invoice_number, r.client_name, r.irn, r.ack_no, r.ewb_no,
        r.einvoice_error, r.ewb_error,
      ].map(v => (v || "").toString().toLowerCase()).join(" | ");
      return haystack.includes(q);
    });
  }, [rows, search, actionFilter, actionOptions]);

  const load = () => {
    setLoading(true);
    const base = t.group === "ei" ? "/gst/einvoices" : "/gst/eway-bills";
    api.get(`${base}?status=${t.status}`).then(r => setRows(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/gst/config").then(r => setCfg(r.data)).catch(() => {}); }, []);
  useEffect(load, [tab]);

  // Sandbox mode → fire directly (test data, safe to play with).
  // Production mode → DO NOT fire. Open the typed-confirmation modal first.
  function generateEInv(row) {
    if (cfg?.api_mode === "production") {
      setProdConfirmText("");
      setProdConfirmFor(row);
      return;
    }
    fireGenerateEInv(row);
  }

  async function fireGenerateEInv(row) {
    try {
      const res = await api.post(`/sales-invoices/${row.id}/einvoice/generate`);
      if (res?.data?.recovered_from_duplicate) {
        const src = res?.data?.recovery_source || "recovery";
        toast.success(`Existing IRN recovered successfully (${src}) for ${row.invoice_number}`);
      } else {
        toast.success(`IRN generated for ${row.invoice_number}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "E-Invoice generation failed");
      load();
    }
  }

  async function confirmProdGenerate() {
    if (prodConfirmText.trim() !== "GENERATE" || !prodConfirmFor) return;
    setProdSubmitting(true);
    const row = prodConfirmFor;
    try {
      const res = await api.post(`/sales-invoices/${row.id}/einvoice/generate`);
      // The backend may have auto-recovered from NIC 2150 (Duplicate IRN):
      //   • inline_dupirn  → NIC embedded the existing IRN in the same
      //                       response (no extra call)
      //   • fetch_by_doc   → NIC didn't embed it, we fetched separately
      // Surface that to the operator so they know the IRN was reconciled,
      // not freshly issued.
      if (res?.data?.recovered_from_duplicate) {
        const src = res?.data?.recovery_source || "recovery";
        toast.success(`Existing IRN recovered successfully (${src}) for ${row.invoice_number}`);
      } else {
        toast.success(`Real IRN generated on NIC for ${row.invoice_number}`);
      }
      setProdConfirmFor(null);
      setProdConfirmText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "E-Invoice generation failed");
      load();
    } finally {
      setProdSubmitting(false);
    }
  }

  // ──────── Failed E-Invoice recovery handlers ────────
  async function confirmFetchByDoc() {
    if (!fetchFor) return;
    setFetchBusy(true);
    const row = fetchFor;
    try {
      const res = await api.get(`/sales-invoices/${row.id}/einvoice/fetch-by-doc`);
      toast.success(`Existing IRN fetched from NIC · ${(res?.data?.einvoice?.irn || "").slice(0, 16)}…`);
      setFetchFor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fetch-by-doc failed");
    } finally {
      setFetchBusy(false);
    }
  }

  // Backfill Signed QR for an IRN already on file (typically one
  // recovered from a duplicate-IRN response which carried IRN + Ack
  // but no QR). Calls NIC "Get E-Invoice Details by IRN".
  async function refreshQr(row) {
    try {
      await api.post(`/sales-invoices/${row.id}/einvoice/refresh-qr`);
      toast.success(`Signed QR refreshed from NIC for ${row.invoice_number}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "QR refresh failed");
    }
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 max-w-[1400px]">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="GST · IRP · E-Way Bill"
        title="GST Centre"
        subtitle={cfg ? `API Mode: ${cfg.api_mode === "sandbox" ? "🧪 SANDBOX (test data, safe to play with)" : "🔴 PRODUCTION (live NIC IRP)"} · Cancellation window: ${cfg.cancel_window_hours}h` : "Loading config…"}
        onRefresh={load} refreshing={loading}
      />

      {/* Tab strip */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-slate-200">
        {TABS.map(x => {
          const Icon = x.icon;
          return (
            <button key={x.key} onClick={() => setTab(x.key)} data-testid={`gst-tab-${x.key}`}
              className={`px-3 py-2 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 ${tab === x.key ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              <Icon size={12} /> {x.label}
            </button>
          );
        })}
      </div>

      {/* Toolbar: search + action-aware filter (added 2026-02-22) */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search Invoice # / Client / IRN / Ack No / EWB / error…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="gst-search"
            className="pl-7 pr-3 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none w-72"
          />
        </div>
        {/* Action-column filter — visible on every tab; lets operators slice
            the list by the action that is actionable on each row (Generate IRN /
            Retry / Fetch / Refresh QR / Cancel / etc.) or by business criteria
            (B2B vs B2C, mandatory ≥ ₹50K threshold, overdue, expiring). The
            label deliberately says "Action" not "Filter" so operators map it
            mentally to the Action column they see on the right. */}
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Action:</span>
        <select
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          data-testid="gst-action-filter"
          className="border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none min-w-[220px]"
        >
          {actionOptions.map(([val, label]) => (
            <option key={val} value={val}>{label}</option>
          ))}
        </select>
        {(search || actionFilter !== "all") && (
          <button
            type="button"
            onClick={() => { setSearch(""); setActionFilter("all"); }}
            data-testid="gst-clear-filters"
            className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-slate-900 hover:underline"
          >
            Clear filters
          </button>
        )}
        <div className="ml-auto text-[11px] uppercase tracking-wider font-bold text-slate-500" data-testid="gst-result-count">
          Showing {filtered.length} of {rows.length}
        </div>
      </div>

      {/* Data list */}
      {loading ? (
        <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-slate-200 bg-slate-50 p-10 text-center">
          <CheckCircle2 size={28} className="text-emerald-600 mx-auto mb-2" />
          <div className="text-sm font-semibold text-slate-700">
            {rows.length === 0 ? "No items in this tab" : "No rows match the current filters"}
          </div>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={() => { setSearch(""); setActionFilter("all"); }}
              className="mt-2 text-xs text-[#F97316] hover:underline"
            >
              Clear filters and show all {rows.length} row{rows.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-xs" data-testid="gst-list">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Invoice #</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-right">Amount</th>
                {t.group === "ei" ? (<>
                  <th className="px-3 py-2 text-left">IRN</th>
                  <th className="px-3 py-2 text-left">Ack No</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </>) : (<>
                  <th className="px-3 py-2 text-left">EWB No</th>
                  <th className="px-3 py-2 text-left">Valid Until</th>
                  <th className="px-3 py-2 text-left">Status</th>
                </>)}
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 font-bold text-slate-900">{r.invoice_number}</td>
                  <td className="px-3 py-1.5 text-slate-700 max-w-[220px] truncate" title={r.client_name}>{r.client_name}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatINR(r.total_amount)}</td>
                  {t.group === "ei" ? (<>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600 truncate max-w-[140px]" title={r.irn}>{r.irn ? r.irn.slice(0, 16) + "…" : "—"}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">{r.ack_no || "—"}</td>
                    <td className="px-3 py-1.5"><StatusPill s={r.einvoice_status} cancelled={r.cancel_status === "Cancelled"} /></td>
                  </>) : (<>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-slate-600">{r.ewb_no || "—"}</td>
                    <td className="px-3 py-1.5 text-[11px] text-slate-600">{r.valid_until || "—"}</td>
                    <td className="px-3 py-1.5"><StatusPill s={r.ewb_status} /></td>
                  </>)}
                  <td className="px-3 py-1.5 text-right">
                    {t.key === "ei_pending" && (
                      <button onClick={() => generateEInv(r)} data-testid={`gst-gen-ei-${r.id}`}
                        className="bg-[#F97316] hover:bg-[#EA580C] text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 inline-flex items-center gap-1">
                        <Send size={10} /> Generate IRN
                      </button>
                    )}
                    {t.key === "ei_failed" && (
                      <div className="inline-flex gap-1">
                        <button onClick={() => generateEInv(r)} data-testid={`gst-retry-ei-${r.id}`}
                          className="bg-[#F97316] hover:bg-[#EA580C] text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 inline-flex items-center gap-1"
                          title="Retry IRN generation">
                          <Send size={10} /> Retry
                        </button>
                        <button onClick={() => setFetchFor(r)} data-testid={`gst-fetch-ei-${r.id}`}
                          className="bg-sky-600 hover:bg-sky-700 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 inline-flex items-center gap-1"
                          title="Fetch existing IRN from NIC (use when error is 2150 Duplicate IRN)">
                          <RefreshCw size={10} /> Fetch
                        </button>
                        <button onClick={() => setManualFor(r)} data-testid={`gst-manual-ei-${r.id}`}
                          className="bg-slate-700 hover:bg-slate-900 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 inline-flex items-center gap-1"
                          title="Manually paste IRN/Ack No/Ack Date from NIC portal">
                          <PenLine size={10} /> Manual
                        </button>
                      </div>
                    )}
                    {t.key === "ei_generated" && r.einvoice_status === "Generated" && (
                      <div className="inline-flex flex-col items-end gap-1">
                        <div className="inline-flex gap-1">
                          {!r.signed_qr_code && (
                            <button onClick={() => refreshQr(r)} data-testid={`gst-refresh-qr-${r.id}`}
                              className="bg-sky-600 hover:bg-sky-700 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 inline-flex items-center gap-1"
                              title="Fetch Signed QR from NIC (use on IRNs recovered from duplicate response)">
                              <RefreshCw size={10} /> Refresh QR
                            </button>
                          )}
                          <button onClick={() => setCancelFor(r)} data-testid={`gst-cancel-ei-${r.id}`}
                            className="bg-rose-600 hover:bg-rose-700 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1">
                            Cancel IRN
                          </button>
                        </div>
                        <DriftBadge value={r.auto_drift_correction} rowId={r.id} />
                      </div>
                    )}
                    {t.key === "ewb_pending" && (
                      <button onClick={() => setEwbFor(r)} data-testid={`gst-gen-ewb-${r.id}`}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] uppercase tracking-wider font-bold px-2 py-1 inline-flex items-center gap-1">
                        <Truck size={10} /> Create E-Way Bill
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cancel IRN modal */}
      {cancelFor && (
        <CancelIRNModal row={cancelFor} onClose={() => setCancelFor(null)} onDone={() => { setCancelFor(null); load(); }} />
      )}
      {/* Fetch Existing IRN from NIC modal */}
      {fetchFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !fetchBusy && setFetchFor(null)}
             data-testid="fetch-irn-modal">
          <div className="bg-white max-w-md w-full p-5 border-t-4 border-sky-600" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-black text-sky-700 flex items-center gap-2">
                <RefreshCw size={18} /> Fetch Existing IRN
              </h3>
              <button onClick={() => !fetchBusy && setFetchFor(null)} disabled={fetchBusy}><X size={16} /></button>
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-sky-700 bg-sky-50 border border-sky-200 p-2 mb-3">
              NIC GetIRNByDocDtls · This queries NIC for an IRN already issued against this invoice number.
            </div>
            <div className="text-xs text-slate-700 mb-3 leading-relaxed">
              Invoice <strong>{fetchFor.invoice_number}</strong> · Client <strong>{fetchFor.client_name}</strong>
              <br />
              Use this when the last attempt failed with <code className="bg-slate-100 px-1 font-mono text-rose-700">NIC 2150 · Duplicate IRN</code>.
              The existing IRN, Ack No, Ack Date and Signed QR will be fetched and saved onto this invoice.
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setFetchFor(null)} disabled={fetchBusy}
                className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold"
                data-testid="fetch-irn-cancel">Cancel</button>
              <button onClick={confirmFetchByDoc} disabled={fetchBusy}
                data-testid="fetch-irn-submit"
                className="bg-sky-600 hover:bg-sky-700 disabled:bg-slate-300 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
                <RefreshCw size={11} /> {fetchBusy ? "Fetching…" : "Fetch from NIC"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Manual IRN Entry modal */}
      {manualFor && (
        <ManualIRNModal
          row={manualFor}
          onClose={() => setManualFor(null)}
          onDone={() => { setManualFor(null); load(); }}
        />
      )}
      {/* Create E-Way Bill modal */}
      {ewbFor && (
        <EWayBillModal row={ewbFor} onClose={() => setEwbFor(null)} onDone={() => { setEwbFor(null); load(); }} />
      )}
      {/* PRODUCTION mode safeguard — typed confirmation before hitting NIC IRP */}
      {prodConfirmFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !prodSubmitting && setProdConfirmFor(null)}
             data-testid="prod-irn-confirm-modal">
          <div className="bg-white max-w-md w-full p-5 border-t-4 border-rose-600" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-lg font-black text-rose-700 flex items-center gap-2">
                <AlertTriangle size={18} /> Production E-Invoice
              </h3>
              <button onClick={() => !prodSubmitting && setProdConfirmFor(null)} disabled={prodSubmitting}>
                <X size={16} />
              </button>
            </div>
            <div className="text-[11px] uppercase tracking-wider font-bold text-rose-700 bg-rose-50 border border-rose-200 p-2 mb-3">
              🔴 LIVE NIC IRP · This will generate a real GST E-Invoice on the NIC portal.
            </div>
            <div className="text-xs text-slate-700 mb-3 leading-relaxed">
              Invoice <strong>{prodConfirmFor.invoice_number}</strong> · Client{" "}
              <strong>{prodConfirmFor.client_name}</strong> · Amount{" "}
              <strong>{formatINR(prodConfirmFor.total_amount)}</strong>
              <br />
              Once generated, this IRN is reported to the Government and cannot be undone
              outside the 24-hour cancellation window.
            </div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">
              Type <code className="bg-slate-100 px-1 text-rose-700 font-mono">GENERATE</code> to confirm
            </label>
            <input
              autoFocus
              value={prodConfirmText}
              onChange={(e) => setProdConfirmText(e.target.value)}
              data-testid="prod-irn-confirm-input"
              placeholder="GENERATE"
              className="w-full text-sm font-mono px-2 py-2 border border-slate-300 mb-3 focus:border-rose-600 focus:outline-none"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setProdConfirmFor(null)}
                disabled={prodSubmitting}
                data-testid="prod-irn-confirm-cancel"
                className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold"
              >
                Cancel
              </button>
              <button
                onClick={confirmProdGenerate}
                disabled={prodSubmitting || prodConfirmText.trim() !== "GENERATE"}
                data-testid="prod-irn-confirm-submit"
                className="bg-rose-600 hover:bg-rose-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1"
              >
                <Send size={11} />
                {prodSubmitting ? "Generating…" : "Generate Real IRN"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPill({ s, cancelled }) {
  const effective = cancelled ? "Cancelled" : s || "Pending";
  const map = {
    Generated: "bg-emerald-100 text-emerald-800",
    Pending:   "bg-amber-100 text-amber-800",
    Failed:    "bg-rose-100 text-rose-800",
    Cancelled: "bg-slate-200 text-slate-700",
  };
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${map[effective] || "bg-slate-100 text-slate-600"}`}>{effective}</span>;
}

function CancelIRNModal({ row, onClose, onDone }) {
  const [reasonCode, setReasonCode] = useState("4");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!remarks.trim()) { toast.error("Remarks are required"); return; }
    setBusy(true);
    try {
      await api.post(`/sales-invoices/${row.id}/einvoice/cancel`, { reason_code: reasonCode, remarks: remarks.trim() });
      toast.success("IRN cancelled · the underlying Sales Invoice is preserved");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-black text-slate-900">Cancel E-Invoice</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="text-xs text-slate-600 mb-3">
          Invoice <strong>{row.invoice_number}</strong> · IRN <code className="text-[10px]">{(row.irn || "").slice(0, 24)}…</code>
        </div>
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 p-2 mb-3">
          ⚠ Per IRP rules, cancellation is allowed only within 24h of generation and only if the underlying invoice has not been transported. The original Sales Invoice voucher will NOT be cancelled.
        </div>
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Reason</label>
        <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} data-testid="cancel-ei-reason"
          className="w-full text-xs px-2 py-2 border border-slate-300 mb-3">
          {CANCEL_REASONS.map(r => <option key={r.code} value={r.code}>{r.code} · {r.label}</option>)}
        </select>
        <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Remarks *</label>
        <textarea rows={3} value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="cancel-ei-remarks"
          placeholder="Detailed reason for cancellation…"
          className="w-full text-xs px-2 py-2 border border-slate-300 mb-3" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 border border-slate-300 text-xs">Close</button>
          <button onClick={submit} disabled={busy} data-testid="cancel-ei-submit"
            className="bg-rose-600 hover:bg-rose-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-60">
            {busy ? "Cancelling…" : "Submit Cancellation"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EWayBillModal({ row, onClose, onDone }) {
  const [f, setF] = useState({
    transporter_name: "", transporter_gstin: "", transport_mode: "Road",
    vehicle_number: "", distance_km: 100, dispatch_from: "", ship_to: "",
    document_number: row.invoice_number, document_date: row.invoice_date || "",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);

  async function submit() {
    for (const k of ["transporter_name", "transport_mode", "distance_km", "dispatch_from", "ship_to"]) {
      if (!f[k]) { toast.error(`${k.replace(/_/g, " ")} is required`); return; }
    }
    setBusy(true);
    try {
      await api.post(`/sales-invoices/${row.id}/eway-bill/generate`, { ...f, distance_km: Number(f.distance_km) });
      toast.success("E-Way Bill generated successfully");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "E-Way Bill generation failed");
    } finally { setBusy(false); }
  }

  const inp = "w-full text-xs px-2 py-1.5 border border-slate-300 focus:border-[#F97316] focus:outline-none";

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-5 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-black text-slate-900">Create E-Way Bill</h3>
          <button onClick={onClose}><X size={16} /></button>
        </div>
        <div className="text-xs text-slate-600 mb-3">
          Invoice <strong>{row.invoice_number}</strong> · IRN <code className="text-[10px]">{(row.irn || "").slice(0, 20)}…</code>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Transporter Name *"><input className={inp} value={f.transporter_name} onChange={e => set("transporter_name", e.target.value)} data-testid="ewb-transporter-name" /></F>
          <F label="Transporter GSTIN"><input className={inp} value={f.transporter_gstin} onChange={e => set("transporter_gstin", e.target.value)} /></F>
          <F label="Transport Mode *">
            <select className={inp} value={f.transport_mode} onChange={e => set("transport_mode", e.target.value)} data-testid="ewb-transport-mode">
              <option>Road</option><option>Rail</option><option>Air</option><option>Ship</option>
            </select>
          </F>
          <F label="Vehicle Number"><input className={inp} value={f.vehicle_number} onChange={e => set("vehicle_number", e.target.value)} placeholder="OD02AB1234" /></F>
          <F label="Distance (km) *"><input type="number" className={inp} value={f.distance_km} onChange={e => set("distance_km", e.target.value)} data-testid="ewb-distance" /></F>
          <F label="Document Number"><input className={inp} value={f.document_number} onChange={e => set("document_number", e.target.value)} /></F>
          <F label="Document Date"><input type="date" className={inp} value={f.document_date} onChange={e => set("document_date", e.target.value)} /></F>
          <F label="Dispatch From *" full><input className={inp} value={f.dispatch_from} onChange={e => set("dispatch_from", e.target.value)} data-testid="ewb-dispatch-from" placeholder="City, State, PIN" /></F>
          <F label="Ship To *" full><input className={inp} value={f.ship_to} onChange={e => set("ship_to", e.target.value)} data-testid="ewb-ship-to" placeholder="City, State, PIN" /></F>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-2 border border-slate-300 text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="ewb-submit"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-60">
            {busy ? "Generating…" : "Generate E-Way Bill"}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children, full }) {
  return <label className={`block ${full ? "md:col-span-2" : ""}`}><span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">{label}</span>{children}</label>;
}

function ManualIRNModal({ row, onClose, onDone }) {
  const [f, setF] = useState({
    irn: "", ack_no: "", ack_date: "",
    signed_qr_code: "", signed_invoice_jwt: "",
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false);

  // NIC IRNs are 64-character SHA-256 hex hashes. We validate length so
  // operators can't accidentally paste an Ack No into the IRN field
  // (a frequent mistake when copying from the NIC portal MIS report).
  const irnValid = f.irn.trim().length >= 32 && /^[A-Fa-f0-9]+$/.test(f.irn.trim());
  const canSubmit = irnValid && f.ack_no.trim() && f.ack_date.trim();

  async function submit() {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post(`/sales-invoices/${row.id}/einvoice/manual-entry`, {
        irn: f.irn.trim(),
        ack_no: f.ack_no.trim(),
        ack_date: f.ack_date.trim(),
        signed_qr_code: f.signed_qr_code.trim(),
        signed_invoice_jwt: f.signed_invoice_jwt.trim(),
      });
      toast.success(`IRN saved manually for ${row.invoice_number}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual entry failed");
    } finally { setBusy(false); }
  }

  const inp = "w-full text-xs px-2 py-1.5 border border-slate-300 focus:border-slate-700 focus:outline-none font-mono";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto"
         onClick={() => !busy && onClose()}
         data-testid="manual-irn-modal">
      <div className="bg-white max-w-lg w-full p-5 my-8 border-t-4 border-slate-700" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-black text-slate-900 flex items-center gap-2">
            <PenLine size={18} /> Manual IRN Entry
          </h3>
          <button onClick={onClose} disabled={busy}><X size={16} /></button>
        </div>
        <div className="text-[11px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 border border-amber-200 p-2 mb-3">
          ⚠ Use only when you have the IRN details from the NIC e-invoice portal (https://einvoice1.gst.gov.in).
          This entry is audited — your email is stored against the record.
        </div>
        <div className="text-xs text-slate-700 mb-3">
          Invoice <strong>{row.invoice_number}</strong> · Client <strong>{row.client_name}</strong>
        </div>
        <div className="grid grid-cols-1 gap-3">
          <F label="IRN * (64-character hex hash from NIC)" full>
            <textarea rows={2} className={inp} value={f.irn} onChange={e => set("irn", e.target.value)}
              data-testid="manual-irn-input" placeholder="e.g. 0a1b2c3d4e5f… (64 chars)" />
            {f.irn && !irnValid && (
              <span className="text-[10px] text-rose-600 mt-1 block">
                IRN must be hexadecimal characters only (0-9, A-F) and at least 32 chars long.
              </span>
            )}
          </F>
          <F label="Ack No *">
            <input className={inp} value={f.ack_no} onChange={e => set("ack_no", e.target.value)}
              data-testid="manual-ack-no" placeholder="e.g. 11200012345" />
          </F>
          <F label="Ack Date * (DD/MM/YYYY HH:MM:SS)">
            <input className={inp} value={f.ack_date} onChange={e => set("ack_date", e.target.value)}
              data-testid="manual-ack-date" placeholder="e.g. 21/02/2026 14:30:00" />
          </F>
          <F label="Signed QR (optional)" full>
            <textarea rows={2} className={inp} value={f.signed_qr_code} onChange={e => set("signed_qr_code", e.target.value)}
              data-testid="manual-qr" placeholder="Paste the Signed QR text if available — required to print the QR on the tax invoice PDF" />
          </F>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold"
            data-testid="manual-irn-cancel">Cancel</button>
          <button onClick={submit} disabled={busy || !canSubmit}
            data-testid="manual-irn-submit"
            className="bg-slate-700 hover:bg-slate-900 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-4 py-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1">
            <PenLine size={11} /> {busy ? "Saving…" : "Save IRN"}
          </button>
        </div>
      </div>
    </div>
  );
}


// =========================================================================
// DriftBadge — audit-trail pill rendered below the IRN Action buttons when
// the e-invoice payload builder auto-absorbed paise-level rounding drift
// into RndOffAmt during IRN generation. A non-zero `auto_drift_correction`
// on the SalesInvoice means: "the system silently reconciled a few paise of
// line-rounding noise so the IRP validator could accept the invoice — for
// your audit trail, here's exactly how much it absorbed."
//
// Renders nothing for invoices that needed no correction (the common case).
// =========================================================================
function DriftBadge({ value, rowId }) {
  const v = Number(value || 0);
  if (Math.abs(v) < 0.005) return null;  // 0 → no badge
  const formatted = Math.abs(v).toFixed(2);
  const sign = v < 0 ? "−" : "+";
  return (
    <div
      data-testid={`gst-drift-badge-${rowId}`}
      title={`Auto-reconcile: line-level CGST/SGST rounding drifted by ${sign}₹${formatted}. The system absorbed it into RndOffAmt so the IRP validator passed cleanly. No action needed — recorded here for your audit trail.`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 text-[9px] uppercase tracking-wider font-bold whitespace-nowrap cursor-help"
    >
      <Info size={9} />
      Auto-corrected {sign}₹{formatted} drift
    </div>
  );
}
